import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Gauge, Receipt, ListChecks, ChatCircleDots,
  Envelope, Coins, Calculator, Buildings, UsersThree, Briefcase, Gear,
  CaretLeft, CaretRight, Scales, MapPin, CalendarBlank,
  Path, Clock, IdentificationCard, FileText, ShieldCheck, Robot, Wrench, Toolbox,
  Lifebuoy, MapTrifold, PushPin, PushPinSlash,
} from "@phosphor-icons/react";

import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import TeamLiveWidget from "./TeamLiveWidget";

/* iter 34 Phase E — every nav item now has a stable `key` (used to
   identify the item in user/org sidebar preferences). Section rows
   (`{sec: ...}`) are positional anchors — they're rendered in their
   default position when the user has not customised, and dropped
   entirely once the user reorders. */
const NAV_CORE = [
  { key: "dashboard", to: "/", icon: Gauge, label: "DASHBOARD" },
  { key: "command-center", to: "/command-center", icon: MapTrifold, label: "Command Center" },
  { key: "messages", to: "/messages", icon: ChatCircleDots, label: "Messaging" },
  { key: "email", to: "/email", icon: Envelope, label: "Email Inbox" },
  { sec: "OPERATIONS" },
  { key: "intake", to: "/intake", icon: Robot, label: "Intake / New Input Review" },
  { key: "jobs", to: "/jobs", icon: Briefcase, label: "Jobs" },
  { key: "routes", to: "/routes", icon: Path, label: "Routes" },
  { key: "calendar", to: "/calendar", icon: CalendarBlank, label: "Calendar" },
  { key: "tasks", to: "/tasks", icon: ListChecks, label: "Tasks" },
  { key: "proofs", to: "/proofs", icon: ListChecks, label: "Proofs" },
  { sec: "RECORDS" },
  { key: "organizations", to: "/organizations", icon: Buildings, label: "Organizations" },
  { key: "subjects", to: "/subjects", icon: IdentificationCard, label: "Subjects" },
  { key: "locations", to: "/locations", icon: MapPin, label: "Locations" },
  { key: "cases", to: "/cases", icon: Scales, label: "Cases" },
  { sec: "FINANCE" },
  { key: "receivables", to: "/receivables", icon: Receipt, label: "Receivables" },
  { key: "payables", to: "/payables", icon: Coins, label: "Payables" },
  { key: "accounting", to: "/accounting", icon: Calculator, label: "Accounting" },
  // iter 44 — WORKSPACE section removed. team / notaries / settings / templates
  // moved into the Settings page or the TopBar (Templates is now a bold-bordered
  // button next to the Settings gear).
];

const NAV_CUSTOMER = [
  { key: "dashboard", to: "/", icon: Gauge, label: "DASHBOARD" },
  { key: "submit-job", to: "/submit-job", icon: Briefcase, label: "Submit a Job" },
  { key: "my-jobs", to: "/my-jobs", icon: ListChecks, label: "My Jobs" },
  { key: "my-invoices", to: "/my-invoices", icon: Receipt, label: "My Invoices" },
  { key: "messages", to: "/messages", icon: ChatCircleDots, label: "Messages" },
  // iter 44 — customer-portal Settings entry also removed; the TopBar gear is
  // now the single entry-point across both role variants.
];

const NAV_CONTRACTOR = [
  { key: "dashboard", to: "/", icon: Gauge, label: "DASHBOARD" },
  { key: "messages", to: "/messages", icon: ChatCircleDots, label: "Messaging" },
  { key: "email", to: "/email", icon: Envelope, label: "Email Inbox" },
  { sec: "OPERATIONS" },
  { key: "jobs", to: "/jobs", icon: Briefcase, label: "Jobs" },
  { key: "routes", to: "/routes", icon: Path, label: "Routes" },
  { key: "calendar", to: "/calendar", icon: CalendarBlank, label: "Calendar" },
  { key: "tasks", to: "/tasks", icon: ListChecks, label: "Tasks" },
  { sec: "RECORDS" },
  { key: "my-hosts", to: "/my-hosts", icon: Buildings, label: "My Hosts" },
  { key: "subjects", to: "/subjects", icon: IdentificationCard, label: "Subjects" },
  { key: "locations", to: "/locations", icon: MapPin, label: "Locations" },
  { key: "cases", to: "/cases", icon: Scales, label: "Cases" },
  { sec: "FINANCE" },
  { key: "receivables", to: "/receivables", icon: Receipt, label: "Receivables" },
  { key: "accounting", to: "/accounting", icon: Calculator, label: "Accounting" },
  // iter 44 — WORKSPACE section removed (also from this contractor-host nav).
];

/* iter 34 Phase E follow-up — Developer-only nav.
 *
 * Pinned to the TOP of the sidebar for any user where
 * `is_developer_user === true` OR `role === 'developer'`. This block is
 * a "safe zone" — it is NEVER passed through user/org preferences and
 * is NEVER shown in the Settings → Sidebar Layout editor. Every key
 * has `devOnly: true` so editor helpers can filter them out.
 */
const NAV_DEV = [
  { sec: "DEVELOPER" },
  { key: "dev", to: "/dev", icon: Wrench, label: "Developer Console", devOnly: true },
  { key: "support", to: "/support", icon: Lifebuoy, label: "Customer Support", devOnly: true },
];

const DEV_KEYS = new Set(NAV_DEV.filter(n => !n.sec).map(n => n.key));
const isDeveloperUser = (user) =>
  !!(user && (user.is_developer_user === true || user.role === "developer"));

/* Apply preferences (user → org default → none) to a base nav array.
 *
 * iter 34 Phase E follow-up — section headers are now first-class
 * entries in `order` using the `header:<text>` key form. This lets each
 * user customise the headers (rename / reorder / remove) without losing
 * them when they tweak the layout.
 *
 * - When `order` is non-empty, the result is built strictly from the
 *   user's order: any item with key `header:Foo` is rendered as a
 *   section row `{sec: "Foo"}`; any known nav key is the matching link.
 *   Unknown / hidden keys are dropped. Nav items not in `order` are
 *   appended at the end (no implicit headers).
 * - When `order` is empty, the result preserves the default section
 *   structure but filters out items whose keys appear in `hidden`. */
function applyPrefs(baseNav, prefs) {
  const order = (prefs?.order || []).filter(Boolean);
  const hidden = new Set((prefs?.hidden || []).filter(Boolean));
  const items = baseNav.filter(n => !n.sec);
  const byKey = new Map(items.map(n => [n.key, n]));

  // iter 49 — auto-migrate saved sidebar preferences for the
  // Customers + Vendors → Organizations consolidation. Replace the first
  // occurrence of "customers" with "organizations" (when not already
  // present) and drop any subsequent "customers" / "vendors" entries.
  // Hidden-set entries for either are also dropped so a user who hid
  // Customers doesn't accidentally hide the new unified Organizations.
  const migrateOrder = (rawOrder) => {
    const seen = new Set();
    const out = [];
    let injected = rawOrder.includes("organizations");
    for (const k of rawOrder) {
      if (k === "customers" || k === "vendors") {
        if (!injected) {
          out.push("organizations");
          injected = true;
          seen.add("organizations");
        }
        continue;
      }
      if (!seen.has(k)) {
        out.push(k);
        seen.add(k);
      }
    }
    return out;
  };
  const orderUse = migrateOrder(order);
  const hiddenUse = new Set([...hidden].filter(k => k !== "customers" && k !== "vendors"));

  if (orderUse.length === 0) {
    // Default layout — keep sections, just filter hidden items.
    return baseNav.filter(n => n.sec || !hiddenUse.has(n.key));
  }
  const seen = new Set();
  const out = [];
  for (const k of orderUse) {
    if (hiddenUse.has(k)) continue;
    if (k.startsWith("header:")) {
      out.push({ sec: k.slice(7).trim() || " " });
      continue;
    }
    const item = byKey.get(k);
    if (item && !seen.has(k)) {
      out.push(item);
      seen.add(k);
    }
  }
  // Append any default items the user hasn't placed (and isn't hiding).
  for (const it of items) {
    if (!seen.has(it.key) && !hiddenUse.has(it.key)) {
      out.push(it);
    }
  }
  // iter 44 — drop "orphan" section headers that have no items underneath
  // them (typically left behind in saved user preferences after we removed
  // the WORKSPACE section). A header is orphaned if it isn't followed by
  // any non-section entry before the next header / end of list.
  const cleaned = [];
  for (let i = 0; i < out.length; i++) {
    const node = out[i];
    if (node.sec) {
      // Peek forward: if the next non-section node before another section
      // doesn't exist, skip this header.
      let hasChild = false;
      for (let j = i + 1; j < out.length; j++) {
        if (out[j].sec) break;
        hasChild = true; break;
      }
      if (!hasChild) continue;
    }
    cleaned.push(node);
  }
  return cleaned;
}

export default function Sidebar() {
  const loc = useLocation();
  const { user } = useAuth();
  const baseNav =
    user?.org_type === "customer" ? NAV_CUSTOMER
    : user?.org_type === "contractor" ? NAV_CONTRACTOR
    : NAV_CORE;
  // Strip dev keys from prefs scope — they live in their own NAV_DEV
  // "safe zone" and are never touched by user/org customisation.
  const baseNavForPrefs = baseNav.filter(n => n.sec || !DEV_KEYS.has(n.key));

  const [prefs, setPrefs] = useState({ order: [], hidden: [], show_team_live: true });
  const fetchPrefs = async () => {
    try {
      const [me, org] = await Promise.all([
        api.get("/me/sidebar-preferences").then(r => r.data).catch(() => null),
        api.get("/org/sidebar-default").then(r => r.data).catch(() => null),
      ]);
      const hasMe = me && ((me.order?.length || 0) > 0 || (me.hidden?.length || 0) > 0);
      // Preserve the user's show_team_live even when their order/hidden
      // are still empty (so hiding the widget without touching layout
      // still persists).
      const base = hasMe ? me : (org || { order: [], hidden: [] });
      const showTeam = me?.show_team_live ?? org?.show_team_live ?? true;
      setPrefs({ ...base, show_team_live: showTeam });
    } catch { /* keep defaults */ }
  };
  useEffect(() => { if (user) fetchPrefs(); }, [user?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for cross-component updates (Settings page broadcasts when the
  // user saves a new preference so the sidebar re-renders without reload).
  useEffect(() => {
    const handler = () => fetchPrefs();
    window.addEventListener("sidebar-prefs-changed", handler);
    return () => window.removeEventListener("sidebar-prefs-changed", handler);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const userNav = applyPrefs(baseNavForPrefs, prefs);
  // Developer "safe zone" — always pinned at the top for dev users, and
  // never shown to anyone else.
  const NAV = isDeveloperUser(user) ? [...NAV_DEV, ...userNav] : userNav;

  // iter 73.16 — Hover-expand + Pin sidebar behavior.
  //
  //  - Default state: `pinned=false` → sidebar renders in mini (w-16) mode.
  //  - Hovering the sidebar expands it to full width (w-60) with a
  //    300ms transition (`transition-[width]`).
  //  - Clicking the pin icon toggles `pinned` — when pinned, the sidebar
  //    stays open regardless of hover state. Persisted to localStorage.
  //  - `collapsed` (the render-time state) is derived from
  //    !(pinned || hovered). The old `sidebar-collapsed` boolean is
  //    migrated so previously-collapsed users get the new mini+hover
  //    default; previously-expanded users become "pinned open".
  const [pinned, setPinned] = useState(() => {
    try {
      const legacy = localStorage.getItem("sidebar-collapsed");
      const stored = localStorage.getItem("sidebar-pinned");
      if (stored !== null) return stored === "1";
      // Migration path — flip legacy `collapsed=false` to `pinned=true`
      // so power users don't lose their always-open sidebar.
      if (legacy === "0") return true;
      return false;
    } catch { return false; }
  });
  const [hovered, setHovered] = useState(false);
  const collapsed = !(pinned || hovered);
  useEffect(() => {
    try { localStorage.setItem("sidebar-pinned", pinned ? "1" : "0"); } catch { /* ignore */ }
  }, [pinned]);

  const togglePin = () => setPinned((v) => !v);

  return (
    <aside
      className={`${collapsed ? "w-16" : "w-60"} border-r border-border bg-card text-foreground flex flex-col h-[calc(100vh-3rem)] sticky top-12 transition-[width] duration-300 ease-out z-[60]`}
      data-testid="sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      data-pinned={pinned ? "true" : "false"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={`border-b border-border ${collapsed ? "p-2" : "p-3"}`}>
        <button
          onClick={togglePin}
          className={`btn btn-ghost btn-sm w-full ${collapsed ? "justify-center" : "justify-between"}`}
          data-testid="sidebar-pin-toggle"
          aria-label={pinned ? "Unpin sidebar" : "Pin sidebar open"}
          aria-pressed={pinned}
          title={pinned ? "Unpin — sidebar will collapse when you move away" : "Pin sidebar open"}
        >
          {collapsed ? (
            // Mini-state chevron doubles as a visual affordance: shows
            // right-caret so users know it will expand.
            <CaretRight size={12} />
          ) : (
            <>
              <span className="text-[10px] mono tracking-wide">
                {pinned ? "PINNED" : "HOVER"}
              </span>
              {pinned
                ? <PushPin size={14} weight="fill" className="text-primary" />
                : <PushPinSlash size={14} weight="regular" className="text-muted-foreground" />}
            </>
          )}
        </button>
      </div>

      {/* iter 73.13 — Team Live widget, pinned at the top. Hidden when
          the user toggles show_team_live=false in Sidebar Preferences. */}
      {prefs.show_team_live !== false && user?.org_id && (
        <TeamLiveWidget orgId={user.org_id} collapsed={collapsed} />
      )}

      <nav className="flex-1 overflow-y-auto py-2">
        {NAV.map((it, i) =>
          it.sec ? (
            collapsed
              ? <div key={`sec-${i}`} className="mx-3 my-2 border-t border-border" />
              : <div key={`sec-${i}`} className="label-xs px-4 mt-4 mb-1">{it.sec}</div>
          ) : (
            <Link
              key={it.key}
              to={it.to}
              className={`sidebar-link ${collapsed ? "sidebar-link--collapsed" : ""} ${loc.pathname === it.to ? "active" : ""}`}
              data-testid={`nav-${it.key}`}
              title={collapsed ? it.label : undefined}
            >
              <it.icon size={collapsed ? 18 : 16} weight={loc.pathname === it.to ? "bold" : "regular"} />
              {!collapsed && <span>{it.label}</span>}
            </Link>
          )
        )}
      </nav>
    </aside>
  );
}

// Export the base NAV maps + dev-key filter so the Settings editor can read
// available keys and exclude the developer "safe zone".
export { NAV_CORE, NAV_CUSTOMER, NAV_CONTRACTOR, DEV_KEYS };
